import invariant from "invariant";
import { classes } from "polytype";
import { omit } from "remeda";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  AggregateMetaData,
  DataModel,
  extractId,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import { Cronjob } from "../../cronjob/index.js";
import type { MySqlDetailed, RedisDetailed } from "../../database/index.js";
import { MySql } from "../../database/index.js";
import { Project } from "../../project/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import type { AppUpdatePolicyData } from "../App/index.js";
import { App } from "../App/index.js";
import { AppLinkedDatabase } from "../AppLinkedDatabase/index.js";
import { AppVersion } from "../AppVersion/index.js";
import { InstalledSystemSoftware } from "../InstalledSystemSoftware/index.js";
import {
  SystemSoftware,
  SystemSoftwareDetailed,
} from "../SystemSoftware/index.js";
import type {
  AppInstallationCopyRequestData,
  AppInstallationCreateRequestData,
  AppInstallationData,
  AppInstallationListItemData,
  AppInstallationListQueryModelData,
  AppInstallationUpdateRequestData,
  AppSavedUserInput,
  InstalledSystemSoftwareQuery,
} from "./types.js";
import { AppIds } from "./types.js";

export class AppInstallation extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "app",
    "appinstallation",
  );

  public static findAggregate(appInstallationId?: string) {
    return appInstallationId
      ? { id: appInstallationId, ...AppInstallation.aggregateMetaData }
      : undefined;
  }

  public static ofId(id: string) {
    return new AppInstallation(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.appInstallation.find(id);
    if (data) {
      return new AppInstallationDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const appInstallation = await this.find(id);
    assertObjectFound(appInstallation, "appInstallation", id);
    return appInstallation;
  });

  public findDetailed = provideReact(
    () => AppInstallation.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<AppInstallationDetailed | undefined>>;

  public getDetailed = provideReact(
    () => AppInstallation.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<AppInstallationDetailed>>;

  public static query(query: AppInstallationListQueryModelData = {}) {
    return new AppInstallationListQuery(query);
  }

  public static async create(
    project: Project,
    data: AppInstallationCreateRequestData,
  ) {
    const response = await config.behaviors.appInstallation.create(
      project.id,
      data,
    );

    if ("id" in response) {
      return new AppInstallation(response.id);
    }

    return response;
  }

  public async update(data: AppInstallationUpdateRequestData) {
    await config.behaviors.appInstallation.update(this.id, data);
  }

  public async addSystemSoftware(
    systemSoftwareId: string,
    systemSoftwareVersionId: string,
    updatePolicy?: AppUpdatePolicyData,
  ) {
    await this.update({
      systemSoftware: {
        [systemSoftwareId]: {
          systemSoftwareVersion: systemSoftwareVersionId,
          updatePolicy: updatePolicy ?? "patchLevel",
        },
      },
    });
  }

  public async updateSystemSoftwareVersion(
    systemSoftwareId: string,
    systemSoftwareVersionId: string,
  ) {
    await this.update({
      systemSoftware: {
        [systemSoftwareId]: {
          systemSoftwareVersion: systemSoftwareVersionId,
        },
      },
    });
  }

  public async deleteSystemSoftware(systemSoftwareId: string) {
    await this.update({
      systemSoftware: {
        [systemSoftwareId]: {},
      },
    });
  }

  public async delete() {
    await config.behaviors.appInstallation.delete(this.id);
  }

  public async linkDatabase(database: MySqlDetailed | RedisDetailed) {
    const mainMySqlUser =
      database instanceof MySql ? database.mainUser : undefined;

    await config.behaviors.appInstallation.linkDatabase(this.id, {
      databaseId: database.id,
      purpose: "custom",
      databaseUserIds: mainMySqlUser ? { admin: mainMySqlUser.id } : undefined,
    });
  }

  public async unlinkDatabase(databaseId: string) {
    await config.behaviors.appInstallation.unlinkDatabase(this.id, databaseId);
  }

  public async copy(data: AppInstallationCopyRequestData) {
    await config.behaviors.appInstallation.copy(this.id, data);
  }

  public getInstalledSystemSoftware = provideReact(
    async (query?: InstalledSystemSoftwareQuery) => {
      const data =
        await config.behaviors.appInstallation.getInstalledSystemSoftware(
          this.id,
          query,
        );
      return data.map((d) => new SystemSoftwareDetailed(d));
    },
    [this.id],
  );

  public findPhp = provideReact(
    async (installedSystemSoftware: InstalledSystemSoftware[]) => {
      const systemSoftwareList = await SystemSoftware.query().execute();

      const phpId = systemSoftwareList.items.find((s) => s.name === "php")?.id;

      return installedSystemSoftware.find((i) => i.systemSoftware.id === phpId);
    },
    [this.id],
  );
}

class AppInstallationCommon extends classes(
  DataModel<AppInstallationData | AppInstallationListItemData>,
  AppInstallation,
) {
  public readonly description: string;
  public readonly execCommand: string;
  public readonly app: App;
  public readonly appVersion: AppVersion;
  public readonly project: Project;
  public readonly isUpdating: boolean;
  public readonly isInstalling: boolean;
  public readonly host?: string;
  public readonly installedSystemSoftware: InstalledSystemSoftware[];
  public readonly installationPath: string;
  public readonly userInputs: AppSavedUserInput[];
  public readonly linkedDatabases: AppLinkedDatabase[];
  public readonly primaryDatabase?: AppLinkedDatabase;
  public readonly shortId: string;
  public readonly customDocumentRoot?: string;
  public readonly entryPointUserInput?: AppSavedUserInput;

  public constructor(data: AppInstallationData | AppInstallationListItemData) {
    super([data], [data.id]);
    this.description = data.description;
    this.execCommand = `app exec ${data.id}`;
    this.app = App.ofId(data.appId);
    this.project = Project.ofId(data.projectId!);
    this.appVersion = AppVersion.ofId(data.appVersion.desired, this.app);
    this.isUpdating =
      !!data.appVersion.current &&
      data.appVersion.current !== data.appVersion.desired;

    this.isInstalling = !data.appVersion.current;

    this.host = data.userInputs?.find((u) => u.name === "host")?.value;

    this.installedSystemSoftware = data.systemSoftware
      ? data.systemSoftware
          .sort((a, b) => a.systemSoftwareId.localeCompare(b.systemSoftwareId))
          .map((s) => new InstalledSystemSoftware(s))
      : [];
    this.installationPath = data.installationPath;
    this.userInputs = data.userInputs ?? [];
    this.linkedDatabases = data.linkedDatabases
      ? data.linkedDatabases.map((d) => new AppLinkedDatabase(d))
      : [];
    this.primaryDatabase = this.linkedDatabases.find((d) => d.isPrimary);
    this.shortId = data.shortId;
    this.customDocumentRoot = data.customDocumentRoot;

    this.entryPointUserInput = this.userInputs.find(
      (u) => u.name === "entrypoint",
    );
  }

  // todo: Query parameter zum filtern nach Apps
  public listCronjobs = provideReact(async () => {
    const cronjobList = await Cronjob.query(this.project).execute();

    return cronjobList.items.filter((i) => i.data.appId === this.id);
  });

  public listCompatibleDatabases = provideReact(async () => {
    const mySqlList = await MySql.query(this.project).execute();

    const appVersionDetailed = await this.appVersion.getDetailed();

    const allowedDatabaseVersions: string[] =
      appVersionDetailed.data.databases?.map((d) => {
        switch (d.version) {
          case "91bbc8ea-9308-4b7f-88dc-1a2873d02c8c":
            return "5.6";
          case "1be01968-784d-434c-aa05-2e64a51ced28":
            return "5.7";
          case "37931c38-e05e-470b-9e22-a035ef9f383b":
            return "8.0";
          default:
            return "";
        }
      }) ?? [];

    return mySqlList.items.filter((d) =>
      allowedDatabaseVersions.includes(d.version),
    );
  });

  public async replacePrimaryDatabase(mySql: MySqlDetailed) {
    const oldDatabaseId =
      this.linkedDatabases.find((d) => d.isPrimary)?.id ?? "";

    invariant(mySql.mainUser, "my sql main user not found");

    await config.behaviors.appInstallation.replacePrimaryDatabase(this.id, {
      oldDatabaseId,
      newDatabaseId: mySql.id,
      databaseUserIds: { admin: mySql.mainUser.id },
    });
  }

  public findMainSystemSoftware = provideReact(
    async (installedSystemSoftware: InstalledSystemSoftware[]) => {
      const systemSoftwareList = await SystemSoftware.query().execute();

      const phpId = systemSoftwareList.items.find((s) => s.name === "php")?.id;
      const nodeId = systemSoftwareList.items.find(
        (s) => s.name === "node",
      )?.id;
      const pythonId = systemSoftwareList.items.find(
        (s) => s.name === "python",
      )?.id;

      if (this.app.id === AppIds.staticFiles) {
        return;
      } else if (this.app.id === AppIds.nodejs) {
        return installedSystemSoftware.find(
          (i) => i.systemSoftware.id === nodeId,
        );
      } else if (this.app.id === AppIds.python) {
        return installedSystemSoftware.find(
          (i) => i.systemSoftware.id === pythonId,
        );
      } else if (phpId) {
        return installedSystemSoftware.find(
          (i) => i.systemSoftware.id === phpId,
        );
      } else if (nodeId) {
        return installedSystemSoftware.find(
          (i) => i.systemSoftware.id === nodeId,
        );
      } else if (pythonId) {
        return installedSystemSoftware.find(
          (i) => i.systemSoftware.id === pythonId,
        );
      }
    },
    [this.id],
  );
}

export class AppInstallationDetailed extends classes(
  AppInstallationCommon,
  DataModel<AppInstallationData>,
) {
  public constructor(data: AppInstallationData) {
    super([data], [data]);
  }
}

export class AppInstallationListItem extends classes(
  AppInstallationCommon,
  DataModel<AppInstallationListItemData>,
) {
  public constructor(data: AppInstallationListItemData) {
    super([data], [data]);
  }
}

export class AppInstallationListQuery extends ListQueryModel<AppInstallationListQueryModelData> {
  public refine(query: AppInstallationListQueryModelData) {
    return new AppInstallationListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const projectId = extractId(this.query.project);

    const { items, totalCount } = projectId
      ? await config.behaviors.appInstallation.list(
          projectId,
          omit(this.query, ["project"]),
        )
      : await config.behaviors.appInstallation.listForUser(this.query);

    return new AppInstallationList(
      this.query,
      items.map((d) => new AppInstallationListItem(d)),
      totalCount,
    );
  }, [this.queryId, "appInstallation"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "appInstallation"]);
}

export class AppInstallationList extends classes(
  AppInstallationListQuery,
  ListDataModel<AppInstallationListItem>,
) {
  public constructor(
    query: AppInstallationListQueryModelData,
    appInstallations: AppInstallationListItem[],
    totalCount: number,
  ) {
    super([query], [appInstallations, totalCount]);
  }
}
